import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, apiError } from "@/lib/api";

const PAGES = [
  { slug: "about-us", title: "About Us" },
  { slug: "contact", title: "Contact" },
  { slug: "shipping-delivery", title: "Shipping and Delivery" },
  { slug: "returns-refunds", title: "Returns and Refunds" },
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "terms-conditions", title: "Terms and Conditions" },
];

const PagesTab = () => {
  const [slug, setSlug] = useState("about-us");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/pages/${slug}`)
      .then((r) => setContent(r.data.content_html || ""))
      .catch((e) => toast.error(apiError(e, "Could not load page")))
      .finally(() => setLoading(false));
  }, [slug]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/pages/${slug}`, { content_html: content });
      toast.success("Page updated — live on the storefront now");
    } catch (e) {
      toast.error(apiError(e, "Could not save page"));
    } finally {
      setSaving(false);
    }
  };

  const active = PAGES.find((p) => p.slug === slug);

  return (
    <div className="mx-auto mt-10 max-w-4xl" data-testid="pages-tab">
      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <FileText size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Storefront Pages</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          Edit the text of each information page — changes appear on the site the moment you save.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
          <div className="flex flex-row flex-wrap gap-2 md:flex-col">
            {PAGES.map((p) => (
              <button
                key={p.slug}
                data-testid={`pages-select-${p.slug}`}
                onClick={() => setSlug(p.slug)}
                className={`border px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  slug === p.slug
                    ? "border-gold bg-gold text-espresso"
                    : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
                }`}
              >
                {p.title}
              </button>
            ))}
          </div>

          <div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">
              Editing: {active?.title}
            </p>
            {loading ? (
              <p className="py-16 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
                Loading…
              </p>
            ) : (
              <div data-testid="pages-editor" className="border border-cream/15 focus-within:border-gold">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={{
                    toolbar: [
                      [{ header: [2, 3, false] }],
                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "clean"],
                    ],
                  }}
                  placeholder={`Write the ${active?.title} page content…`}
                />
              </div>
            )}
            <button
              data-testid="pages-save-button"
              onClick={save}
              disabled={saving || loading}
              className="mt-4 bg-gold px-10 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Page"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagesTab;
